import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Integration, IntegrationPlatform, IntegrationPlatformCustomField, IntegrationPlatformCustomFieldType, IntegrationCRMUser, SetupDataResponse, CustomFieldConfiguration, SetupDataChannel, CRMUserMapping, UserMapping, PlatformUserMapping, UpdateCRMUserMapping } from 'voxloud-types/dist/models/integrations';
import { CRMUserMappings } from 'voxloud-types/dist/models/integrations';
import { IntegrationsService } from 'src/app/domains/integrations/services/integrations.service';
import { catchError } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { SelectTeamOutput } from 'src/app/shared/components/integration-select-team/integration-select-team.component';
import { CUSTOM_FIELDS_NAME, SetupDataValues } from 'src/app/domains/integrations/models/integrations.model';
import { UsersService } from 'src/app/domains/users/services';
import { Company, IPbxUser } from 'voxloud-types/dist/models';

@Component({
  selector: 'gdp-edit-integration-popup',
  templateUrl: './edit-integration-popup.component.html',
  styleUrls: ['./edit-integration-popup.component.scss']
})
export class EditIntegrationPopupComponent implements OnInit {
  @Output() onUpdateIntegration: EventEmitter<Integration> = new EventEmitter<Integration>();
  @Input() company: Company;
  public form: FormGroup;
  public integration: Integration;
  public customFields: IntegrationPlatformCustomField[];
  public loading: boolean;
  public customFieldsFormGroup: FormGroup;
  public crmUsers: IntegrationCRMUser[];
  public channels: SetupDataChannel[];
  public setupData: SetupDataResponse;
  public isEmptyCF: boolean;
  public setupDataError: boolean;
  public mappingsError: boolean;
  public isSaving: boolean;
  public CUSTOM_FIELDS_NAME = CUSTOM_FIELDS_NAME;
  public mappings: CRMUserMapping[] = [];
  public users: IPbxUser[]
  public allAvailableUsers;
  public mappingsGroup = new FormControl([]);
  public DEFAULT_FIELD_TYPES = [
    'AUTOMATIC_CONTACT_CREATION',
    'DEFAULT_USER_ID', 'CHANNEL_ID',
    'TEAM_ID', 'TEAMS_CHANNEL_ID',
    'DEPARTMENT_ID',
    'WORK_TYPE_ID',
    'USER_MAPPING',
    'LIST_ID'
  ];


  constructor(
    private activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private integrationService: IntegrationsService,
    private notification: NotificationService,
    private userService: UsersService
  ) { }

  ngOnInit() {
    this.loading = true;
    forkJoin({
      platform: this.integrationService.getPlatformById(this.integration?.platform_id).pipe(catchError(() => of(undefined))),
      setupData: this.integrationService.getSetupData(this.integration?.id).pipe(catchError(() => { this.setupDataError = true; return of(undefined); })),
      mappings: this.integrationService.getUserMappings(this.integration?.id).pipe(catchError(() => { this.mappingsError = true; return of(undefined); })),
      pbxUsers: this.userService.getUsersFromPbxById(this.company?.pbxId).pipe(catchError(() => of(undefined)))
    }).subscribe((res: {
        platform: IntegrationPlatform,
        setupData: SetupDataResponse,
        mappings: CRMUserMappings,
        pbxUsers: IPbxUser[]
      }) => {
        if (!res.platform) {
          this.loading = false;
          return;
        }
        this.customFields = res.platform.custom_fields;
        this.setupData = res.setupData;
        this.isEmptyCF = res.platform.custom_fields.length === 0;
        this.mappings = res.mappings?.user_mappings;
        this.crmUsers = res.setupData?.users ?? [];
        this.users = res.pbxUsers;
        this.buildEditForm();
        this.allAvailableUsers = { availableUsers: this.users, availableCrmUsers: this.crmUsers };
        this.loading = false;
      },
      () => {
        this.loading = false;
      });
  }

  buildEditForm() {
    const custom_fields: Record<string, FormControl> = {};

    this.form = this.fb.group({
      name: new FormControl<string>(
        this.integration.name,
        [Validators.required]
      )
    });

    this.customFields?.forEach((f: IntegrationPlatformCustomField) => {
      let defaultValue = f.default_value || '';
      const value = this.integration.custom_fields.find(v => v.name === f.name)?.value || '';

      switch (f.name) {
        case 'DEFAULT_USER_ID':
          defaultValue = this.setupData?.users?.[0]?.external_id ?? '';
          break;

        case 'CHANNEL_ID':
        case 'DEPARTMENT_ID':
        case 'WORK_TYPE_ID':
        case 'LIST_ID':
          if (this.setupData) {
            const values = (this.setupData[CUSTOM_FIELDS_NAME[f.name] as keyof SetupDataResponse] as SetupDataChannel[]) ?? [];
            const id = values.find((v: SetupDataChannel) => v.id === value);
            custom_fields[f.name] = new FormControl<SetupDataChannel | null>(
              id ?? null,
              values.length > 0 ? Validators.required : null
            );
          }
          return;

        case 'USER_MAPPING':
          custom_fields[f.name] = new FormControl(this.mappings);
          return;

        case 'TEAM_ID':
          custom_fields[f.name] = new FormControl<SelectTeamOutput>(
            {
              TEAM_ID: this.integration.custom_fields.find(v => v.name === 'TEAM_ID')?.value ?? '',
              TEAMS_CHANNEL_ID: this.integration.custom_fields.find(v => v.name === 'TEAMS_CHANNEL_ID')?.value ?? '',
            },
            Validators.required
          );
          return;

        case 'TEAMS_CHANNEL_ID':
          return;
      }

      const result = value !== '' ? value : defaultValue;

      switch (f.field_type) {
        case IntegrationPlatformCustomFieldType.BOOLEAN:
          custom_fields[f.name] = new FormControl<boolean>(
            result !== '' ? JSON.parse(result) : false,
            Validators.required
          );
          break;
        case IntegrationPlatformCustomFieldType.TEXT:
        case IntegrationPlatformCustomFieldType.PASSWORD:
          custom_fields[f.name] = new FormControl<string>(
            result,
            Validators.required
          );
          break;
        case IntegrationPlatformCustomFieldType.ARRAY:
          //TODO: to be implemented
          break;
      }
    });

    this.form.addControl('custom_fields', new FormGroup({ ...custom_fields }));
    this.customFieldsFormGroup = this.form.get('custom_fields') as FormGroup;
  }

  isFieldValid(name: string) {
    const control = this.form.get(name);
    return !!(control && !control.valid && control.touched);
  }

  public selectValue(customField: string, value: SetupDataValues): void {
    this.form.get(`custom_fields.${customField}`)?.setValue(value);
  }

  controlSort(_a: string, _b: string) {
    return 0;
  }

  close() {
    this.activeModal.close();
  }

  save() {
    this.isSaving = true;
    let submitData = { ...this.form.value };
    let custom_fields = { ...this.form.value?.custom_fields, ...this.form.value?.custom_fields?.TEAM_ID };
    if (custom_fields != undefined) {
      const customFieldsKeys = Object.keys(custom_fields);
      const idBasedKeys = ['CHANNEL_ID', 'DEPARTMENT_ID', 'WORK_TYPE_ID', 'LIST_ID'];

      submitData.custom_fields = customFieldsKeys.map((key) => {
        const field = this.integration.custom_fields.find((v) => v.name === key);
        const configuration = field?.configuration ?? CustomFieldConfiguration.CUSTOMER;

        let value;
        if (idBasedKeys.includes(key)) {
          value = custom_fields[key]?.id;
        } else if (key === 'DEFAULT_USER_ID') {
          value = custom_fields[key]?.external_id ?? custom_fields[key];
        } else {
          value = custom_fields[key];
        }

        return { name: key, value, configuration };
      });

      submitData.custom_fields = submitData.custom_fields.map((f: any) => {
        if (f.name === 'USER_MAPPING') {
          f.value = 'managed by mapping API';
        }
        return f;
      });
    }

    const mappings = this.integration.custom_fields.find(f => f.name === 'USER_MAPPING');
    if (mappings && !this.mappingsError) {
      forkJoin({
        mappings: this.integrationService.saveUserMappings(this.integration.id,
          (custom_fields.USER_MAPPING as unknown as any[]).map(m => ({
            user_id: m.user.id,
            platform_user_id: m.platform_user.external_id
          })) as UpdateCRMUserMapping[]
        ),
        integration: this.integrationService.updateIntegration(this.integration.id, submitData)
      }).subscribe(
        () => {
          this.onUpdateIntegration.emit();
          this.isSaving = false;
          this.close();
        },
        (err) => {
          if (err.status === 400) {
            this.notification.error('company.integrations.notifications.error.updated');
          } else {
            this.notification.error('common.errors.oops');
          }
          this.isSaving = false;
          this.close();
        }
      );
    } else {
      this.integrationService.updateIntegration(this.integration.id, submitData).subscribe(
        () => {
          this.onUpdateIntegration.emit();
          this.isSaving = false;
          this.close();
        },
        (err) => {
          if (err.status === 400) {
            this.notification.error('company.integrations.notifications.error.updated');
          } else {
            this.notification.error('common.errors.oops');
          }
          this.isSaving = false;
          this.close();
        }
      );
    }
  }

  public get hideTitle(): boolean {
    if (!this.customFieldsFormGroup) return false;
    const conditionalKeys = ['CHANNEL_ID', 'WORK_TYPE_ID', 'DEPARTMENT_ID', 'LIST_ID'];
    return !Object.keys(this.customFieldsFormGroup.controls).some(key => {
      if (conditionalKeys.includes(key)) {
        const setupKey = CUSTOM_FIELDS_NAME[key as keyof typeof CUSTOM_FIELDS_NAME] as keyof SetupDataResponse;
        return !!(this.setupData?.[setupKey])?.length;
      }
      return true;
    });
  }

}